// Single source of truth for the RCE field paths that the Kill Sheet
// consumes (and freezes into its Engineering Snapshot).
//
// Lives in its own file so the Inheritance Audit diagnostic
// (`lib/rce/inheritance.js`) can import it WITHOUT depending on
// `lib/killsheet/storage.js`. Storage re-exports it under the original
// `_KILLSHEET_CONSUMES_FOR_AUDIT` name for symmetry.
//
// RCE-4.5 — Engineering Context Unification: extended to cover every
// inheritable Kill Sheet input. Removing dual pipelines so there is exactly
// one canonical inheritance pathway.

export const KILLSHEET_CONSUMES = Object.freeze([
  // Project identity
  "project.operator", "project.client", "project.country", "project.field",
  // Rig identity + pump output
  "rig.name", "rig.contractor",
  "rig.pumpOutputBblStk",
  "rig.surfaceLineVolBbl",
  // Well identity + geometry + LOT
  "well.name", "well.environment", "well.waterDepthFt",
  "well.holeSection", "well.holeSizeIn",
  "well.currentMdFt", "well.currentTvdFt",
  "well.casingShoeMdFt", "well.casingShoeTvdFt",
  "well.stringInternalVolBbl", "well.bitToShoeVolBbl", "well.shoeToSurfaceVolBbl",
  "well.lotPressurePsi", "well.mwAtLotPpg",
  // BHA + Drill String identity
  "bha.id", "bha.revision",
  "drillString.id", "drillString.revision",
  // Active Mud Program
  "mud.weightInPpg", "mud.fluidBasis",
  // Settings — engineering defaults
  "settings.safetyMarginPpg",
  "settings.scheduleIntervalStrokes",
  "settings.wellControlStandard",
]);

// Audit alias — read-only mirror used by the Inheritance Audit diagnostic.
export const _KILLSHEET_CONSUMES_FOR_AUDIT = KILLSHEET_CONSUMES;

// Mapping from RCE field path → Kill Sheet record path. Used by the
// unified `synchronizeKillSheet` action to populate `rec.inputs.*` and
// `rec.basis.*` from a frozen Engineering Snapshot. Paths NOT in this
// map are display-only on the snapshot strip (e.g. project.operator).
export const KILLSHEET_SNAPSHOT_TO_INPUT = Object.freeze({
  // Well geometry
  "well.currentMdFt":          "inputs.currentMdFt",
  "well.currentTvdFt":         "inputs.currentTvdFt",
  "well.holeSizeIn":           "inputs.holeSize",
  "well.casingShoeTvdFt":      "inputs.casingShoeTvdFt",
  // Volumes (RCE-4.5)
  "well.stringInternalVolBbl": "inputs.stringInternalVolBbl",
  "well.bitToShoeVolBbl":      "inputs.bitToShoeVolBbl",
  "well.shoeToSurfaceVolBbl":  "inputs.shoeToSurfaceVolBbl",
  // LOT (RCE-4.5)
  "well.lotPressurePsi":       "inputs.lotPressurePsi",
  "well.mwAtLotPpg":           "inputs.mwAtLotPpg",
  // Rig (RCE-4.5)
  "rig.pumpOutputBblStk":      "inputs.pumpOutputBblStk",
  "rig.surfaceLineVolBbl":     "inputs.surfaceLineVolBbl",
  // Mud
  "mud.weightInPpg":           "inputs.mwInPpg",
  "mud.fluidBasis":            "basis.fluidBasis",
  // Settings (RCE-4.5)
  "settings.safetyMarginPpg":         "inputs.safetyMarginPpg",
  "settings.scheduleIntervalStrokes": "inputs.scheduleIntervalStrokes",
  "settings.wellControlStandard":     "basis.wellControlStandard",
});
